/** Great rivers: water cut ACROSS a map rather than grown as a zone.
 *
 * Every other kind of water on this map is a zone - a Voronoi blob that grew
 * from a seed - and a blob cannot be a river. A river is long, narrow, and it
 * goes somewhere: it divides the board, and the interesting question it asks is
 * where the crossings are. Grown from a seed it comes out as a pond.
 *
 * So the `rivers` water shape cuts channels instead. A channel is a meandering
 * path from one edge of the map to another, widened to two or three cells; the
 * path is the cheapest route under a noise field, which is what makes it wander
 * instead of running straight, and it is the same trick the road network uses
 * to stop looking generated.
 *
 * Crossings are not placed here, and deliberately. `repairConnectivity` already
 * knows how to bridge water that has cut the land in two - three cells wide, so
 * it reads as an isthmus - and it runs after this. A channel that splits the
 * map therefore comes back with a ford in it, in the cheapest place, which is
 * exactly where a ford belongs.
 */

import type { AdventureMapState } from "@game/core";
import { setTerrain } from "@game/core";
import { cheapestPath, terrainOf, xOf, yOf, type Grid } from "./grid.js";
import { NOISE_ONE, relief } from "./noise.js";
import { type Rng, int } from "./rng.js";

/** Salt for the meander field, so a channel does not follow the road noise. */
const RIVER_SALT = 0.618;
/** How strongly the ground steers the channel. Higher wanders further. */
const MEANDER = 900;
/** Half-width. 1 is a three-cell channel, which a hero cannot wade. */
const HALF_WIDTH = 1;

/**
 * Cut `count` channels edge to edge. Returns how many cells became water.
 *
 * Runs before the seam and beach passes, so the banks are painted like any
 * other coast, and before the mainland repair, so a channel that isolates a
 * region gets its ford.
 */
export function carveChannels(
  m: AdventureMapState, r: Rng, count: number,
): number {
  const g: Grid = { width: m.width, height: m.height };
  let cut = 0;
  for (let k = 0; k < count; k++) {
    const [from, to] = endpoints(g, r);
    // Cost is the meander field alone: every land cell is passable to a river,
    // and what decides the line is which way the ground falls.
    const path = cheapestPath(g, from, to, (i) => {
      if (terrainOf(m, i) === "water") return 1;
      return 1 + Math.trunc(relief(xOf(g, i), yOf(g, i), RIVER_SALT) * MEANDER / NOISE_ONE);
    });
    if (path === null) continue;
    for (const i of path) cut += widen(m, g, i);
  }
  return cut;
}

/** Two points on opposite edges, so the channel crosses the whole board. */
function endpoints(g: Grid, r: Rng): [number, number] {
  const vertical = int(r, 2) === 0;
  const span = vertical ? g.width : g.height;
  const a = 2 + int(r, Math.max(1, span - 4));
  const b = 2 + int(r, Math.max(1, span - 4));
  return vertical
    ? [a, (g.height - 1) * g.width + b]
    : [a * g.width, b * g.width + (g.width - 1)];
}

/** One cell of channel, and its banks. */
function widen(m: AdventureMapState, g: Grid, cell: number): number {
  const cx = xOf(g, cell), cy = yOf(g, cell);
  let cut = 0;
  for (let dy = -HALF_WIDTH; dy <= HALF_WIDTH; dy++) {
    for (let dx = -HALF_WIDTH; dx <= HALF_WIDTH; dx++) {
      const x = cx + dx, y = cy + dy;
      if (x < 0 || y < 0 || x >= g.width || y >= g.height) continue;
      if (terrainOf(m, y * g.width + x) === "water") continue;
      setTerrain(m, x, y, 0, "water");
      cut++;
    }
  }
  return cut;
}
